import React, { useState, useEffect } from 'react';
import { Settings, Plus, X, Save, AlertTriangle } from 'lucide-react';
import { KPIService } from '../../services/kpiService';

/**
 * Editor for correction reasons settings.
 * Only visible to users where canEditCorrectionReasons() returns true.
 * Allows adding/removing reasons from both categories:
 * - Counts Against Builder
 * - Does Not Count
 */
const CorrectionReasonsEditor = ({ userEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reasons, setReasons] = useState({
    counts_against: [],
    does_not_count: []
  });
  const [originalReasons, setOriginalReasons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newReason, setNewReason] = useState({ counts_against: '', does_not_count: '' });

  // Fetch current reasons
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        setLoading(true);
        const data = await KPIService.getCorrectionReasons();
        setReasons({
          counts_against: data.counts_against || [],
          does_not_count: data.does_not_count || []
        });
        setOriginalReasons({
          counts_against: [...(data.counts_against || [])],
          does_not_count: [...(data.does_not_count || [])]
        });
        setLastUpdated({
          at: data.updatedAt,
          by: data.updatedByEmail
        });
      } catch (err) {
        console.error('Error fetching correction reasons:', err);
        setError('Failed to load correction reasons');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchReasons();
    }
  }, [isOpen]);

  // Check if there are unsaved changes
  const hasChanges = originalReasons && (
    JSON.stringify(reasons.counts_against) !== JSON.stringify(originalReasons.counts_against) ||
    JSON.stringify(reasons.does_not_count) !== JSON.stringify(originalReasons.does_not_count)
  );

  // Add a reason to a category
  const handleAddReason = (category) => {
    const value = newReason[category].trim();
    if (!value) return;
    if (reasons[category].includes(value)) {
      setError(`"${value}" already exists in this category`);
      return;
    }
    // Check if it exists in the other category
    const otherCategory = category === 'counts_against' ? 'does_not_count' : 'counts_against';
    if (reasons[otherCategory].includes(value)) {
      setError(`"${value}" already exists in the other category`);
      return;
    }

    setReasons(prev => ({
      ...prev,
      [category]: [...prev[category], value]
    }));
    setNewReason(prev => ({ ...prev, [category]: '' }));
    setError(null);
    setSuccess(false);
  };

  // Remove a reason from a category
  const handleRemoveReason = (category, reason) => {
    setReasons(prev => ({
      ...prev,
      [category]: prev[category].filter(r => r !== reason)
    }));
    setSuccess(false);
  };

  // Save changes
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await KPIService.updateCorrectionReasons(reasons, userEmail);
      setOriginalReasons({
        counts_against: [...reasons.counts_against],
        does_not_count: [...reasons.does_not_count]
      });
      setLastUpdated({
        at: new Date().toISOString(),
        by: userEmail
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving correction reasons:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Reset to original
  const handleReset = () => {
    if (originalReasons) {
      setReasons({
        counts_against: [...originalReasons.counts_against],
        does_not_count: [...originalReasons.does_not_count]
      });
      setError(null);
      setSuccess(false);
    }
  };

  return (
    <>
      <button
        className="kpi-settings-button"
        onClick={() => setIsOpen(true)}
        title="Edit Correction Reasons"
      >
        <Settings size={18} />
        <span>Correction Settings</span>
      </button>

      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-4)'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '800px',
              width: '95%',
              maxHeight: '85vh',
              backgroundColor: 'var(--gray-900)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--gray-700)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-4)',
              borderBottom: '1px solid var(--gray-700)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <Settings size={20} style={{ color: 'var(--gray-400)' }} />
                <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--gray-100)', margin: 0 }}>
                  Correction Reasons Settings
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--gray-400)',
                  cursor: 'pointer',
                  padding: 'var(--space-1)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {loading ? (
              <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--gray-400)' }}>
                Loading...
              </div>
            ) : (
              <>
                {/* Body */}
                <div style={{ padding: 'var(--space-4)', overflowY: 'auto', flex: 1 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    {/* Counts Against Builder */}
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.05)',
                      border: '1px solid var(--red-900)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-4)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-3)'
                      }}>
                        <AlertTriangle size={16} style={{ color: 'var(--red-400)' }} />
                        <span style={{ fontWeight: 600, color: 'var(--red-400)' }}>Counts Against Builder</span>
                      </div>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', marginBottom: 'var(--space-3)' }}>
                        Affects first-pass approval rate
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                        {reasons.counts_against.map((reason, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'var(--gray-800)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--gray-200)'
                          }}>
                            <span>{reason}</span>
                            <button
                              onClick={() => handleRemoveReason('counts_against', reason)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--gray-500)',
                                cursor: 'pointer',
                                padding: '2px'
                              }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <input
                          type="text"
                          value={newReason.counts_against}
                          onChange={(e) => setNewReason(prev => ({ ...prev, counts_against: e.target.value }))}
                          placeholder="Add new reason..."
                          onKeyDown={(e) => e.key === 'Enter' && handleAddReason('counts_against')}
                          style={{
                            flex: 1,
                            padding: 'var(--space-2)',
                            fontSize: 'var(--text-sm)',
                            background: 'var(--gray-800)',
                            border: '1px solid var(--gray-600)',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--gray-100)'
                          }}
                        />
                        <button
                          onClick={() => handleAddReason('counts_against')}
                          disabled={!newReason.counts_against.trim()}
                          style={{
                            padding: 'var(--space-2)',
                            background: 'var(--red-600)',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            color: 'white',
                            cursor: newReason.counts_against.trim() ? 'pointer' : 'not-allowed',
                            opacity: newReason.counts_against.trim() ? 1 : 0.5
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Does Not Count */}
                    <div style={{
                      background: 'rgba(34, 197, 94, 0.05)',
                      border: '1px solid var(--green-900)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-4)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-3)'
                      }}>
                        <span style={{ fontWeight: 600, color: 'var(--green-400)' }}>Does Not Count Against Builder</span>
                      </div>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)', marginBottom: 'var(--space-3)' }}>
                        No KPI impact
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                        {reasons.does_not_count.map((reason, index) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 'var(--space-2) var(--space-3)',
                            background: 'var(--gray-800)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--gray-200)'
                          }}>
                            <span>{reason}</span>
                            <button
                              onClick={() => handleRemoveReason('does_not_count', reason)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'var(--gray-500)',
                                cursor: 'pointer',
                                padding: '2px'
                              }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <input
                          type="text"
                          value={newReason.does_not_count}
                          onChange={(e) => setNewReason(prev => ({ ...prev, does_not_count: e.target.value }))}
                          placeholder="Add new reason..."
                          onKeyDown={(e) => e.key === 'Enter' && handleAddReason('does_not_count')}
                          style={{
                            flex: 1,
                            padding: 'var(--space-2)',
                            fontSize: 'var(--text-sm)',
                            background: 'var(--gray-800)',
                            border: '1px solid var(--gray-600)',
                            borderRadius: 'var(--radius-sm)',
                            color: 'var(--gray-100)'
                          }}
                        />
                        <button
                          onClick={() => handleAddReason('does_not_count')}
                          disabled={!newReason.does_not_count.trim()}
                          style={{
                            padding: 'var(--space-2)',
                            background: 'var(--green-600)',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            color: 'white',
                            cursor: newReason.does_not_count.trim() ? 'pointer' : 'not-allowed',
                            opacity: newReason.does_not_count.trim() ? 1 : 0.5
                          }}
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Error/Success Messages */}
                  {error && (
                    <div style={{
                      marginTop: 'var(--space-4)',
                      padding: 'var(--space-3)',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid var(--red-500)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--red-400)',
                      fontSize: 'var(--text-sm)'
                    }}>
                      {error}
                    </div>
                  )}
                  {success && (
                    <div style={{
                      marginTop: 'var(--space-4)',
                      padding: 'var(--space-3)',
                      background: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid var(--green-500)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--green-400)',
                      fontSize: 'var(--text-sm)'
                    }}>
                      Changes saved successfully!
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-4)',
                  borderTop: '1px solid var(--gray-700)',
                  background: 'var(--gray-850, var(--gray-800))'
                }}>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)' }}>
                    {lastUpdated?.at && (
                      <span>
                        Last updated by {lastUpdated.by || 'unknown'} on{' '}
                        {new Date(lastUpdated.at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    {hasChanges && (
                      <button
                        onClick={handleReset}
                        disabled={saving}
                        style={{
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--text-sm)',
                          background: 'transparent',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-300)',
                          cursor: 'pointer'
                        }}
                      >
                        Reset
                      </button>
                    )}
                    <button
                      onClick={handleSave}
                      disabled={!hasChanges || saving}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--text-sm)',
                        background: hasChanges ? 'var(--primary-500)' : 'var(--gray-700)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        color: hasChanges ? 'white' : 'var(--gray-500)',
                        cursor: hasChanges && !saving ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CorrectionReasonsEditor;
